import React, { useState, useEffect, useRef } from 'react';
import { galleriesAPI, imagesAPI } from '../../services/api';
import '../admin/AdminComponents.css';

const CONCURRENCY = 3; // simultaneous uploads

const GalleryForm = ({ gallery, defaultBrand = 'veloura', onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState(defaultBrand);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [images, setImages] = useState([]);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  // bulk upload state
  const [uploadQueue, setUploadQueue] = useState(null); // { total, done, failed }
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (gallery) {
      setTitle(gallery.title || '');
      setBrand(gallery.brand || defaultBrand);
      setThumbnailUrl(gallery.thumbnail || '');
      setDisplayOrder(gallery.display_order || 0);
      setImages(gallery.images || []);
    } else {
      setBrand(defaultBrand);
    }
  }, [gallery, defaultBrand]);

  const handleThumbnailUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const response = await imagesAPI.uploadLandingImage(file);
      if (response.success && response.data) {
        const imageUrl = response.data.url || response.data.path;
        setThumbnailUrl(imageUrl);
      } else {
        alert(response.message || 'Error uploading thumbnail');
      }
    } catch (error) {
      alert(error.message || 'Error uploading thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleImagesUpload = async (fileList) => {
    if (!fileList || fileList.length === 0 || !gallery?.id) return;

    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) {
      alert('Please select image files only (jpeg, png, gif, webp)');
      return;
    }
    const oversized = files.filter(f => f.size > 15 * 1024 * 1024);
    if (oversized.length > 0) {
      alert(`${oversized.length} file(s) exceed 15MB and will be skipped.`);
    }
    const valid = files.filter(f => f.size <= 15 * 1024 * 1024);
    if (valid.length === 0) return;

    setUploadQueue({ total: valid.length, done: 0, failed: 0 });

    // Upload individually with a concurrency limit so the counter ticks per photo
    let done = 0;
    let failed = 0;
    let index = 0;

    const uploadNext = async () => {
      while (index < valid.length) {
        const file = valid[index++];
        try {
          await galleriesAPI.addImage(gallery.id, file);
          done++;
        } catch {
          failed++;
        }
        setUploadQueue({ total: valid.length, done, failed });
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, uploadNext));

    // Refresh image list once all batches done
    try {
      const galleryResponse = await galleriesAPI.getById(gallery.id);
      if (galleryResponse.success) setImages(galleryResponse.data.images || []);
    } catch {}

    setUploadQueue(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = async (imageIndex) => {
    if (!gallery?.id) return;

    const imageToRemove = images[imageIndex];
    if (!imageToRemove) return;

    let imageId = null;
    if (typeof imageToRemove === 'object' && imageToRemove.id) {
      imageId = imageToRemove.id;
    } else {
      try {
        const galleryResponse = await galleriesAPI.getById(gallery.id);
        if (galleryResponse.success && galleryResponse.data.images) {
          const imageObj = galleryResponse.data.images.find(img =>
            (typeof img === 'object' ? img.url : img) === (typeof imageToRemove === 'object' ? imageToRemove.url : imageToRemove)
          );
          if (imageObj && imageObj.id) {
            imageId = imageObj.id;
          }
        }
      } catch (error) {
        console.error('Error fetching gallery for image ID:', error);
      }
    }

    if (!imageId) {
      alert('Could not find image ID. Please refresh and try again.');
      return;
    }

    if (window.confirm('Are you sure you want to remove this image from the gallery?')) {
      try {
        const response = await galleriesAPI.removeImage(gallery.id, imageId);
        if (response.success) {
          const galleryResponse = await galleriesAPI.getById(gallery.id);
          if (galleryResponse.success) {
            setImages(galleryResponse.data.images || []);
          }
        } else {
          alert(response.message || 'Failed to remove image');
        }
      } catch (error) {
        alert(error.message || 'Failed to remove image');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('Title is required');
      return;
    }

    if (!thumbnailUrl) {
      alert('Thumbnail is required');
      return;
    }

    setLoading(true);
    try {
      const galleryData = {
        brand,
        title: title.trim(),
        thumbnailUrl,
        displayOrder: parseInt(displayOrder) || 0
      };

      let response;
      if (gallery) {
        response = await galleriesAPI.update(gallery.id, galleryData);
      } else {
        response = await galleriesAPI.create(galleryData);
      }

      if (response.success) {
        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        alert(response.message || 'Failed to save gallery');
      }
    } catch (error) {
      alert(error.message || 'Failed to save gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedImageIndex === null || draggedImageIndex === dropIndex || !gallery?.id) return;

    const newImages = [...images];
    const [removed] = newImages.splice(draggedImageIndex, 1);
    newImages.splice(dropIndex, 0, removed);

    try {
      setImages(newImages);
    } catch (error) {
      alert('Failed to reorder images');
    }

    setDraggedImageIndex(null);
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{gallery ? 'Edit Gallery' : 'Create Gallery'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>

        </div>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-group">
            <label className="admin-form-label">Brand *</label>
            <select
              className="admin-form-select"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              required
              disabled={!!gallery}
            >
              <option value="veloura">Veloura</option>

              <option value="vague">Vague</option>
            </select>
            {gallery && <p className="admin-form-hint">Brand cannot be changed after creation</p>}
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Title *</label>

            <input
              type="text"
              className="admin-form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Gallery title"
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Thumbnail *</label>
            {thumbnailUrl && (
              <div className="admin-image-preview-container" style={{ marginBottom: '1rem' }}>
                <img
                  src={thumbnailUrl}
                  alt="Thumbnail preview"
                  className="admin-image-preview"
                  style={{ maxHeight: '200px', width: 'auto' }}
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleThumbnailUpload(e.target.files[0])}
              disabled={uploadingThumbnail}
              style={{ marginBottom: '0.5rem' }}
            />
            {uploadingThumbnail && <p>Uploading...</p>}
            <input
              type="text"
              className="admin-form-input"
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              placeholder="Or enter image URL"
              required
            />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label">Display Order</label>
            <input
              type="number"
              className="admin-form-input"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              min="0"
            />
          </div>
          {gallery && (
            <div className="admin-form-group">
              <label className="admin-form-label">Gallery Images</label>
              <div className="admin-gallery-images-list">
                {images.map((img, index) => {
                  const imageUrl = typeof img === 'object' ? img.url : img;
                  return (
                    <div
                      key={typeof img === 'object' ? img.id : index}
                      className="admin-gallery-image-item"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <img src={imageUrl} alt={`Gallery image ${index + 1}`} />
                      <button
                        type="button"
                        className="admin-icon-button danger"
                        onClick={() => handleRemoveImage(index)}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImagesUpload(e.target.files)}
                  disabled={!!uploadQueue}
                />
                {uploadQueue && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{
                      height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', marginBottom: '0.4rem'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.round(((uploadQueue.done + uploadQueue.failed) / uploadQueue.total) * 100)}%`,
                        background: uploadQueue.failed > 0 ? '#dc143c' : 'var(--accent)',
                        transition: 'width 0.3s ease',
                        borderRadius: '3px'
                      }} />
                    </div>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                      Uploading {uploadQueue.done + uploadQueue.failed} / {uploadQueue.total}
                      {uploadQueue.failed > 0 && ` (${uploadQueue.failed} failed)`}
                    </p>
                  </div>
                )}
                <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>
                  Select multiple images at once — hold Ctrl/Cmd or Shift to select many.
                </p>
              </div>

            </div>

          )}

          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="admin-button primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : gallery ? 'Update' : 'Create'}
            </button>

          </div>

        </form>

      </div>

    </div>

  );
};

export default GalleryForm;

